import { storeToRefs } from 'pinia'
import { watch } from 'vue'

import {
  isBackgroundBlurEnabled,
  parseBackgroundBlurStrength,
  PreferenceKey,
  usePreferencesStore,
} from '@/entities/preference'
import { useWebRTC } from '@/entities/webrtc'

import { updateBackgroundBlurStrengthOnActive } from './maybe-apply-background-blur'
import { RAW_PUBLISH_SETTLE_MS } from './raw-publish-settle'
import { reapplyBackgroundBlurOnActiveSessions } from './reapply-background-blur-on-sessions'

type PreviewRestartHandler = () => void | Promise<void>

let isWatchStarted = false
const previewRestartHandlers = new Set<PreviewRestartHandler>()

const startGlobalWatch = () => {
  if (isWatchStarted) return
  isWatchStarted = true

  const preferencesStore = usePreferencesStore()
  const { preferences } = storeToRefs(preferencesStore)
  const { sessions } = useWebRTC()

  watch(
    () => isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED]),
    async (enabled, wasEnabled) => {
      if (enabled === wasEnabled) return
      if (sessions.value.size) {
        await reapplyBackgroundBlurOnActiveSessions()
      }
      await Promise.all([...previewRestartHandlers].map((h) => h()))
    },
  )

  watch(
    () => parseBackgroundBlurStrength(preferences.value[PreferenceKey.BACKGROUND_BLUR_STRENGTH]),
    (strength) => {
      if (!isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED])) return
      updateBackgroundBlurStrengthOnActive(strength)
    },
  )

  // After answer/offer or mid-call camera on, localVideoStream gets a raw track.
  // Debounce: swapping to canvas.captureStream immediately after publish freezes
  // SFU remotes ~30s. Camera-on paths also call upgrade with the same settle.
  // immediate: watch often starts after the offer track already exists (calling dialog /
  // call footer mount late) — without it, 1:1 outbound video never gets blur.
  let trackWatchTimer: number | undefined
  watch(
    () => Array.from(sessions.value.values())
      .map((session) => session.localVideoStream.value?.getVideoTracks()[0]?.id ?? '')
      .join('|'),
    (trackIds, prevTrackIds) => {
      if (!trackIds || trackIds === prevTrackIds) return
      if (!isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED])) return
      if (!sessions.value.size) return

      if (trackWatchTimer !== undefined) {
        window.clearTimeout(trackWatchTimer)
      }
      trackWatchTimer = window.setTimeout(() => {
        trackWatchTimer = undefined
        void reapplyBackgroundBlurOnActiveSessions()
      }, RAW_PUBLISH_SETTLE_MS)
    },
    { immediate: true },
  )
}

export const watchBackgroundBlurPreference = (options?: {
  onPreviewRestart?: PreviewRestartHandler
}) => {
  startGlobalWatch()
  const handler = options?.onPreviewRestart
  if (handler) previewRestartHandlers.add(handler)
  return () => {
    if (handler) previewRestartHandlers.delete(handler)
  }
}
